import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useVideoMode } from '../../context';

// YouTube Video ID from: https://www.youtube.com/shorts/sfgZsP_xIJk (VIII Tryout Nacional)
const YOUTUBE_VIDEO_ID = 'sfgZsP_xIJk';

export function VideoPlayer() {
  const { isVideoMode, exitVideoMode } = useVideoMode();

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVideoMode) {
        exitVideoMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVideoMode, exitVideoMode]);

  // Prevent body scroll when video is open
  useEffect(() => {
    if (isVideoMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVideoMode]);

  return (
    <AnimatePresence>
      {isVideoMode && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
        >
          {/* Cinema backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/95"
            onClick={exitVideoMode}
          />

          {/* Cinema screen frame */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0, rotateX: 20 }}
            animate={{ scale: 1, opacity: 1, rotateX: 0 }}
            exit={{ scale: 0.8, opacity: 0, rotateX: 20 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-[90vw] max-w-5xl aspect-video rounded-xl overflow-hidden shadow-2xl"
            style={{
              boxShadow: '0 0 100px rgba(34, 197, 94, 0.3), 0 0 200px rgba(251, 191, 36, 0.2)',
            }}
          >
            {/* Video frame glow */}
            <motion.div 
              className="absolute -inset-1 bg-gradient-to-r from-pitch-500 via-brand-yellow to-pitch-500 rounded-xl opacity-60 blur-md"
              animate={{ 
                opacity: [0.4, 0.7, 0.4],
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: 'easeInOut' 
              }}
            />
            
            {/* YouTube iframe container */}
            <div className="relative w-full h-full bg-black rounded-xl overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&showinfo=0&color=white`}
                title="BTalentStar - Video Promocional"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ border: 'none' }}
              />
            </div>

            {/* Decorative corners */}
            <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-brand-yellow/70 rounded-tl-lg pointer-events-none" />
            <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-brand-yellow/70 rounded-tr-lg pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-brand-yellow/70 rounded-bl-lg pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-brand-yellow/70 rounded-br-lg pointer-events-none" />
          </motion.div>

          {/* Close button */}
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ delay: 0.8, type: 'spring' }}
            onClick={exitVideoMode}
            className="absolute top-6 right-6 w-14 h-14 rounded-full bg-brand-navy/80 hover:bg-brand-navy backdrop-blur-sm flex items-center justify-center transition-all hover:scale-110 border border-brand-yellow/30 hover:border-brand-yellow/60 group"
          >
            <X className="w-7 h-7 text-white group-hover:text-brand-yellow transition-colors" />
          </motion.button>

          {/* Title badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.9 }}
            className="absolute top-6 left-6 px-4 py-2 rounded-full bg-brand-navy/80 backdrop-blur-sm border border-brand-yellow/30"
          >
            <span className="text-white font-semibold text-sm">
              🎬 BTalentStar - Video Promocional
            </span>
          </motion.div>

          {/* Back instruction */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm flex items-center gap-2"
          >
            <kbd className="px-2 py-1 rounded bg-white/10 text-xs font-mono">ESC</kbd>
            <span>o clic fuera del video para volver</span>
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
